import { useContext, useEffect, useState } from 'react';
import Slider from '@mui/material/Slider';
import { UMInputNumber } from '@urbanmetrix/um-kit';
import CustomTooltip from 'components/tooltip/CustomTooltip';
import { FAR_TERRAIN_RESOLUTION_MARKS, INTERMEDIATE_TERRAIN_LENGTH_LIMITS } from 'constants/geodata';
import { GeodataContext } from 'contexts/GeodataContext';
import { GridContext } from 'contexts/GridContext';
import {
  getFarTerrainLengthErrorMessage,
  getFarTerrainLengthLimits,
  getIntermediateTerrainLengthErrorMessage,
  isFarTerrainLengthValid,
  isIntermediateTerrainLengthValid,
} from './geodataNumericValidation';

interface TerrainProps {
  readOnly?: boolean;
  closeTerrainDisabled?: boolean;
}

type TerrainPanelState = {
  close: boolean;
  intermediate: boolean;
  far: boolean;
};

const intermediateResolutionMarks = [
  { value: 5, label: '5' },
  { value: 10, label: '10' },
  { value: 15, label: '15' },
  { value: 20, label: '20' },
  { value: 25, label: '25' },
];

const closeResolutionMarks = [
  { value: 0.5, label: '0.5' },
  { value: 1, label: '1' },
  { value: 2, label: '2' },
  { value: 3, label: '3' },
  { value: 5, label: '5' },
];

const initialOpenPanels: TerrainPanelState = {
  close: false,
  intermediate: false,
  far: false,
};

const closeTerrainTooltip = 'The terrain under the Property Border.';

const intermediateTerrainTooltip =
  'Usually related to the area with buildings and trees. The length depends on the site characteristics but 500m is usually a good average value to start with.';

const farTerrainTooltip = 'The Lengths should be defined to include the mountains horizon line';

const Terrain = ({ readOnly = false, closeTerrainDisabled = true }: TerrainProps) => {
  const { geodataParams, setGeodataParams } = useContext(GeodataContext);
  const { gridParams, setGridParams } = useContext(GridContext);
  const [openPanels, setOpenPanels] = useState<TerrainPanelState>(initialOpenPanels);
  const [closeResolution, setCloseResolution] = useState(Number(gridParams?.terrainResolutionClose ?? 0.5));
  const [midResolution, setMidResolution] = useState(Number(geodataParams?.resolutionMid ?? 5));
  const [farResolution, setFarResolution] = useState(Number(geodataParams?.resolutionFar ?? 180));
  const farTerrainLengthLimits = getFarTerrainLengthLimits(farResolution);
  const isLengthMidValid = isIntermediateTerrainLengthValid(geodataParams?.lengthMid);
  const isLengthFarValid = isFarTerrainLengthValid(geodataParams?.lengthFar, farResolution);
  const hasIntermediateTerrainError = !isLengthMidValid;
  const hasFarTerrainError = !isLengthFarValid;

  const togglePanel = (panel: keyof typeof openPanels) => {
    setOpenPanels((prev) => ({
      ...prev,
      [panel]: !prev[panel],
    }));
  };

  const handleGeodataInputChange = (name: string | undefined, value: number | string) => {
    setGeodataParams({ [String(name)]: value });
  };

  useEffect(() => {
    setCloseResolution(Number(gridParams?.terrainResolutionClose ?? 0.5));
  }, [gridParams?.terrainResolutionClose]);

  useEffect(() => {
    setMidResolution(Number(geodataParams?.resolutionMid ?? 5));
  }, [geodataParams?.resolutionMid]);

  useEffect(() => {
    setFarResolution(Number(geodataParams?.resolutionFar ?? 180));
  }, [geodataParams?.resolutionFar]);

  return (
    <div className="geodata-section-block geodata-section-block--terrain">
      <button
        type="button"
        className={`geodata-subaccordion__trigger ${closeTerrainDisabled ? 'geodata-subaccordion__trigger--disabled' : ''}`}
        onClick={closeTerrainDisabled ? undefined : () => togglePanel('close')}
        disabled={closeTerrainDisabled}
      >
        <span className="geodata-accordion__icon">{openPanels.close && !closeTerrainDisabled ? '▾' : '▸'}</span>
        <span className="geodata-slider-field__label--with-info">
          <span>Close Terrain</span>
          <CustomTooltip title={closeTerrainTooltip} arrow />
        </span>
      </button>
      {openPanels.close && !closeTerrainDisabled && (
        <div className="geodata-subaccordion__content">
          <div className="geodata-slider-field">
            <div className="geodata-slider-field__label geodata-slider-field__label--with-info geodata-slider-field__label--resolution">
              <span>Resolution [m]</span>
            </div>
            <Slider
              disabled={readOnly}
              min={0.5}
              max={5}
              step={null}
              marks={closeResolutionMarks}
              value={closeResolution}
              onChange={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setCloseResolution(nextValue);
              }}
              onChangeCommitted={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setGridParams({ terrainResolutionClose: nextValue });
              }}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        className={`geodata-subaccordion__trigger${hasIntermediateTerrainError ? ' geodata-subaccordion__trigger--error' : ''}`}
        onClick={() => togglePanel('intermediate')}
      >
        <span className="geodata-accordion__icon">{openPanels.intermediate ? '▾' : '▸'}</span>
        <span className="geodata-slider-field__label--with-info">
          <span>Intermediate Terrain</span>
          <CustomTooltip title={intermediateTerrainTooltip} arrow />
        </span>
      </button>
      {openPanels.intermediate && (
        <div className="geodata-subaccordion__content">
          <div className="geodata-slider-field">
            <div className="geodata-slider-field__label geodata-slider-field__label--with-info geodata-slider-field__label--resolution">
              <span>Resolution [m]</span>
            </div>
            <Slider
              disabled={readOnly}
              min={5}
              max={25}
              step={5}
              marks={intermediateResolutionMarks}
              value={midResolution}
              onChange={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setMidResolution(nextValue);
              }}
              onChangeCommitted={(_, value) =>
                handleGeodataInputChange('resolutionMid', Array.isArray(value) ? value[0] : value)
              }
            />
          </div>
          <div className="geodata-inline-number">
            <UMInputNumber
              label={
                <span className="geodata-slider-field__label geodata-slider-field__label--with-info">
                  <span>Length [m]</span>
                  <CustomTooltip title={intermediateTerrainTooltip} arrow />
                </span>
              }
              name="lengthMid"
              value={geodataParams?.lengthMid}
              width={84}
              maxErrorMessageWidth={252}
              onChange={handleGeodataInputChange}
              defaultValue={500}
              min={INTERMEDIATE_TERRAIN_LENGTH_LIMITS.min}
              max={INTERMEDIATE_TERRAIN_LENGTH_LIMITS.max}
              errorMessage={getIntermediateTerrainLengthErrorMessage()}
              valid={isLengthMidValid}
              disabled={readOnly}
            />
          </div>
        </div>
      )}

      <button
        type="button"
        className={`geodata-subaccordion__trigger${hasFarTerrainError ? ' geodata-subaccordion__trigger--error' : ''}`}
        onClick={() => togglePanel('far')}
      >
        <span className="geodata-accordion__icon">{openPanels.far ? '▾' : '▸'}</span>
        <span className="geodata-slider-field__label--with-info">
          <span>Far Terrain</span>
          <CustomTooltip title={farTerrainTooltip} arrow />
        </span>
      </button>
      {openPanels.far && (
        <div className="geodata-subaccordion__content">
          <div className="geodata-slider-field">
            <div className="geodata-slider-field__label geodata-slider-field__label--with-info geodata-slider-field__label--resolution">
              <span>Resolution [m]</span>
            </div>
            <Slider
              disabled={readOnly}
              min={30}
              max={450}
              step={null}
              marks={FAR_TERRAIN_RESOLUTION_MARKS}
              value={farResolution}
              onChange={(_, value) => {
                const nextValue = Array.isArray(value) ? value[0] : value;
                setFarResolution(nextValue);
              }}
              onChangeCommitted={(_, value) =>
                handleGeodataInputChange('resolutionFar', Array.isArray(value) ? value[0] : value)
              }
            />
          </div>
          <div className="geodata-inline-number">
            <UMInputNumber
              label={
                <span className="geodata-slider-field__label geodata-slider-field__label--with-info">
                  <span>Length [m]</span>
                  <CustomTooltip title={farTerrainTooltip} arrow />
                </span>
              }
              name="lengthFar"
              value={geodataParams?.lengthFar}
              width={84}
              maxErrorMessageWidth={252}
              onChange={handleGeodataInputChange}
              defaultValue={5000}
              min={farTerrainLengthLimits.min}
              max={farTerrainLengthLimits.max}
              errorMessage={getFarTerrainLengthErrorMessage(farResolution)}
              valid={isLengthFarValid}
              disabled={readOnly}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default Terrain;
